import { StandardEvents } from '@shopify/events';

if (!window.__frPdpPurchaseDetails) {
  window.__frPdpPurchaseDetails = true;

  const tiers = [
    { spend: 250, save: 50 },
    { spend: 350, save: 75 },
    { spend: 450, save: 100 },
  ];
  let refreshTimer;

  const currencyContext = () => {
    const { active = 'USD', rate = '1' } = window.Shopify?.currency || {};
    const conversion = Number(rate);
    if (!Number.isFinite(conversion) || conversion <= 0) return null;
    try {
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: active, minimumFractionDigits: 0, maximumFractionDigits: 2,
      });
      return { active, conversion, format: (usd) => formatter.format(usd * conversion) };
    } catch { return null; }
  };

  const progressFor = (subtotal) => {
    if (subtotal < 250) return (subtotal / 250) / 6;
    if (subtotal < 350) return 1 / 6 + ((subtotal - 250) / 100) / 3;
    if (subtotal < 450) return 1 / 2 + ((subtotal - 350) / 100) / 3;
    return 1;
  };

  const renderTiers = (subtotalInUsd) => {
    const currency = currencyContext();
    if (!currency) return;
    document.querySelectorAll('main .fr-pdp-spend-tiers').forEach((panel) => {
      const next = tiers.find((tier) => subtotalInUsd < tier.spend);
      const message = next
        ? `Spend ${currency.format(next.spend - subtotalInUsd)} more to unlock ${currency.format(next.save)} off`
        : `${currency.format(100)} off unlocked in your bag`;
      panel.querySelector('[data-fr-tier-message]').textContent = message;
      const progress = Math.round(Math.max(0, Math.min(1, progressFor(subtotalInUsd))) * 100);
      panel.querySelector('[data-fr-tier-fill]').style.width = `${progress}%`;
      const track = panel.querySelector('[role="progressbar"]');
      track.setAttribute('aria-valuenow', String(progress));
      track.setAttribute('aria-valuetext', message);
      panel.querySelectorAll('[data-fr-tier-level]').forEach((milestone, index) => {
        const tier = tiers[index];
        const unlocked = subtotalInUsd >= tier.spend;
        milestone.classList.toggle('is-unlocked', unlocked);
        milestone.classList.toggle('is-next', tier === next);
        milestone.querySelector('[data-fr-tier-label]').textContent = `${currency.format(tier.save)} off`;
        milestone.querySelector('[data-fr-tier-status]').textContent = unlocked ? 'Unlocked' : '';
      });
    });
  };

  const refreshCartProgress = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      const currency = currencyContext();
      if (!currency || cart.currency !== currency.active) return;
      const subtotalCents = (cart.items || []).reduce((sum, item) => {
        // The gift app can add a paid-price accessory and discount it to zero.
        if (item.final_line_price === 0 && item.original_line_price > 0) return sum;
        return sum + (Number(item.original_line_price) || 0);
      }, 0);
      renderTiers(subtotalCents / 100 / currency.conversion);
    } catch (error) { console.warn('[fr-pdp-purchase-details] Cart progress unavailable:', error); }
  };

  const scheduleCartRefresh = (delay = 150) => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCartProgress, delay);
  };

  const placeInstallments = () => {
    document.querySelectorAll('main product-price .installments').forEach((installments) => {
      const section = installments.closest('.shopify-section');
      const more = section?.querySelector('product-form-component shopify-accelerated-checkout more-payment-options-link');
      if (!more) return;
      installments.slot = 'more-options';
      installments.dataset.frPriceBlock = installments.closest('product-price')?.dataset.blockId || '';
      more.before(installments);
    });
  };

  const refreshInstallments = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const section = event.target.closest('.shopify-section');
    if (!section || !section.querySelector('.fr-pdp-spend-tiers')) return;
    event.promise.then(({ detail }) => {
      if (!detail?.html) return;
      const current = section.querySelector('shopify-accelerated-checkout .installments[data-fr-price-block]');
      const blockId = current?.dataset.frPriceBlock || section.querySelector('product-price')?.dataset.blockId;
      const next = detail.html.querySelector(`product-price[data-block-id="${blockId}"] .installments`);
      if (!next) { current?.remove(); return; }
      const more = section.querySelector('product-form-component shopify-accelerated-checkout more-payment-options-link');
      if (!more) return;
      next.slot = 'more-options';
      next.dataset.frPriceBlock = blockId || '';
      const nextId = detail.resource?.id;
      if (nextId) {
        const input = next.querySelector('input[name="id"]');
        if (input) input.value = nextId;
        next.querySelector('shopify-payment-terms')?.setAttribute('variant-id', String(nextId));
      }
      if (current) current.replaceWith(next);
      else more.before(next);
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[fr-pdp-purchase-details] Payment terms update failed:', error);
    });
  };

  renderTiers(0);
  placeInstallments();
  [150, 600, 1500].forEach((ms) => setTimeout(placeInstallments, ms));
  const panel = document.querySelector('main .fr-pdp-spend-tiers');
  if (panel && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); refreshCartProgress(); }
    }, { rootMargin: '700px' });
    observer.observe(panel);
  } else { scheduleCartRefresh(400); }
  document.addEventListener(StandardEvents.cartUpdate, () => scheduleCartRefresh());
  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('add-to-cart-component, [name="add"], .cart-drawer')) {
      scheduleCartRefresh(1000);
    }
  });
  window.addEventListener('focus', () => scheduleCartRefresh(300));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleCartRefresh(300); });
  document.addEventListener(StandardEvents.productSelect, refreshInstallments);
  document.addEventListener('shopify:section:load', () => { placeInstallments(); scheduleCartRefresh(); });
}
